import React from "react";
import { RoutedModalProps } from "./RoutedModal";

export interface RoutedModalsContextType {
    routedModals: RoutedModals;
}

export const RoutedModalsContext = React.createContext<RoutedModalsContextType | null>(null);

const ROUTED_MODAL = 'routedModal';
const SLOT = 'slot';

interface RouteWithParams {
    route: string;
    slot?: number;
    params: Record<string, string>;
}

interface RoutedModalsProps {

    /**
     * Triggers both on URL changed by user or programmatically.
     * 
     * I don't know if this is useful in prod; however it was handy during tests, in order
     * to avoid monkeypatching `history.pushState()` / `history.replaceState()` (again, for tests).
     */
    onHistoryChange?: (event?: Event) => void
}

interface RoutedModalsState {
    routesWithParams: RouteWithParams[];
}

export class RoutedModals extends React.Component<RoutedModalsProps, RoutedModalsState> {

    protected static INSTANCE?: RoutedModals;

    protected origPushState: typeof window.history.pushState;
    protected origReplaceState: typeof window.history.replaceState;

    protected modalsByRoute: Record<string, RoutedModalProps> = {};

    contextValue: RoutedModalsContextType = {
        routedModals: this
    };

    constructor(props: {}) {
        super(props);

        if (RoutedModals.INSTANCE) {
            throw new Error("Multiple <RoutedModals> are not needed, and not supported. Please keep only one, usually near the root component of your app.");
        }
        RoutedModals.INSTANCE = this;

        this.handleHistoryChange = this.handleHistoryChange.bind(this);

        // this triggers only for changes done by the user; 
        // NOT also for changes done programmatically
        window.addEventListener("popstate", this.handleHistoryChange);

        // the only way to react to changes done programmatically is to monkeypatch
        // these functions
        this.origPushState = window.history.pushState;
        window.history.pushState = (...args) => {
            this.origPushState.apply(window.history, args as any);
            this.handleHistoryChange();
        };

        this.origReplaceState = window.history.replaceState;
        window.history.replaceState = (...args) => {
            this.origReplaceState.apply(window.history, args as any);
            this.handleHistoryChange();
        };

        // we need to parse the routes initially also: the case where the user types the URL in browser
        const initialRoutes = this.parseRoutes(new URLSearchParams(window.location.search));
        this.state = { routesWithParams: initialRoutes };
        this.replaceUrlWithMaybeCompactedQueryString(initialRoutes);
    }
    
    protected handleHistoryChange(event?: Event) {
        const routeWithParams = this.parseRoutes(new URLSearchParams(window.location.search));
        this.setState({ routesWithParams: routeWithParams });
        this.replaceUrlWithMaybeCompactedQueryString(routeWithParams);

        this.props.onHistoryChange?.(event);
    };

    componentWillUnmount() {
        window.removeEventListener("popstate", this.handleHistoryChange);
        window.history.pushState = this.origPushState;
        window.history.replaceState = this.origReplaceState;
        RoutedModals.INSTANCE = undefined;
    }
    
    addRoutedModal(route: string, props: RoutedModalProps) {
        if (this.modalsByRoute[route]) {
            throw new Error(`The route = ${route} is already being used`);
        }
        this.modalsByRoute[route] = props;
    };

    removeRoutedModal(route: string) {
        delete this.modalsByRoute[route];
    }

    protected saveCurrentRoute(routeMap: Map<string, RouteWithParams>, currentRoute: string, currentSlot: number | undefined, currentParams: Record<string, string>) {
        const routeKey = currentSlot !== undefined ? `${currentRoute}:${currentSlot}` : currentRoute;
        const routeObj: RouteWithParams = {
            route: currentRoute,
            params: { ...currentParams }
        };
        if (currentSlot !== undefined) {
            routeObj.slot = currentSlot;
        }
        routeMap.set(routeKey, routeObj);
    }

    /**
     * W/ AI.
     * 
     * E.g. 1/ For `?routedModal=myEditor&id=1&duplicate=true`, the function returns `[{ route: "myEditor", params: { id: "1", duplicate: true } }]`.
     * 
     * This is practically a command that says: please open the `Modal` (popup, window) registered
     * for route = `myEditor`. All the following params are passed to the modal, in this case, only `id`.
     * 
     * E.g. 2/ For `?routedModal=myEditor&id=1&duplicate=true&routedModal=myOtherEditor&id=2`, we have:
     * `[{ route: "myEditor", params: { id: "1", duplicate: true } }, { route: "myOtherEditor", params: { id: "2" } }]`.
     * 
     * We can observe that after a `routedModal`, all the params until the next `routedModal` are given.
     * 
     * E.g. 3/ For `?routedModal=myEditor&id=1&duplicate=true&routedModal=myOtherEditor&id=2&routedModal=myEditor&id=3`, we have:
     * `[{ route: "myOtherEditor", params: { id: "2" } }, { route: "myEditor", params: { id: "3" } }]`.
     * 
     * If there are multiple `routedModal`s w/ the same value, the last one "wins". This means: if an editor is opened for an ID, we'll not open a new
     * editor. We'll keep only one, but w/ the new ID.
     * 
     * E.g. 4/ `?routedModal=myEditor&id=1&duplicate=true&routedModal=myOtherEditor&id=2&routedModal=myEditor&slot=1&id=3` we have:
     * `[{ route: "myEditor", params: { id: "1", duplicate: true } }, { route: "myOtherEditor", params: { id: "2" } }, { route: "myEditor", slot: 1, params: { id: "3" } }]`.
     * 
     * If there is a `slot`, this means: multiple editors for the same route can exist. So, for different slots, there is no more "the last one wins". 
     * But for the same slot, there is "the last one wins". No slot defined in the URL <-> `slot = undefined`.
     */
    parseRoutes(urlParams: URLSearchParams): RouteWithParams[] {
        const routeMap = new Map<string, RouteWithParams>();

        let currentRoute: string | null = null;
        let currentSlot: number | undefined = undefined;
        let currentParams: Record<string, string> = {};
        const paramsBeforeRoutes: Record<string, any> = {};

        // Iterate through all URL parameters
        for (const [key, value] of urlParams.entries()) {
            if (key === ROUTED_MODAL) {
                // Save previous route if exists
                if (currentRoute) {
                    this.saveCurrentRoute(routeMap, currentRoute, currentSlot, currentParams);
                }

                // Start new route
                currentRoute = value;
                currentSlot = undefined;
                currentParams = {};
            } else if (currentRoute) {
                if (key === SLOT) {
                    currentSlot = parseInt(value, 10);
                } else {
                    // Add parameter to current route
                    currentParams[key] = value;
                }
            } else {
                paramsBeforeRoutes[key] = value;
            }
        }

        // Don't forget the last route
        if (currentRoute) {
            this.saveCurrentRoute(routeMap, currentRoute, currentSlot, currentParams);
        }

        return Array.from(routeMap.values());
    }

    /**
     * W/ AI.
     * 
     * Converts the argument back to query string.
     * 
     * @param currentLocationSearch Should be fed w/ `window.location.search`. It's a param (instead of using this expression): for testing purposes.
     *  We take all the params BEFORE the first route. And we include them in the result BEFORE the content of `routesWithParams`. The reason: maybe the
     *  URL has search params that are not specific to `RoutedModals` & co. We need to keep them. Maybe/probably some piece of code (other than us) needs it.
     */
    routesToQueryString(routesWithParams: RouteWithParams[], currentLocationSearch: string): string {
        const searchParams = new URLSearchParams();

        // 1) Keep params that appear BEFORE the first routedModal from the current URL
        if (currentLocationSearch) {
            const currentParams = new URLSearchParams(currentLocationSearch);
            for (const [key, value] of currentParams.entries()) {
                if (key === ROUTED_MODAL) break;
                searchParams.append(key, value);
            }
        }

        // 2) Append routed modals and their parameters
        for (const route of routesWithParams) {
            searchParams.append(ROUTED_MODAL, route.route);

            if (route.slot !== undefined) {
                searchParams.append(SLOT, String(route.slot));
            }

            for (const [key, value] of Object.entries(route.params)) {
                searchParams.append(key, value);
            }
        }

        const query = searchParams.toString();
        return query ? `?${query}` : '';
    }

    replaceUrlWithMaybeCompactedQueryString(routesWithParams: RouteWithParams[]) {
        const url = new URL(window.location.href);
        url.search = this.routesToQueryString(routesWithParams, window.location.search);
        this.origReplaceState.call(window.history, {}, "", url.toString());
    }

    /**
     * Looks for the `RouteWithParams` corresponding to the args. It removes it and puts it
     * in the URL. This will lead to closing the corresponding Modal.
     */
    replaceUrlToCloseARoutedModal(route: string, slot?: number) {
        const routesWithParams = this.state.routesWithParams.filter(r => r.route !== route || r.slot !== slot);
        const url = new URL(window.location.href);
        url.search = this.routesToQueryString(routesWithParams, window.location.search);
        history.pushState({}, "", url.toString());
    }

    render() {
        return (
            <RoutedModalsContext.Provider value={this.contextValue}>
                {this.props.children}
                {this.state.routesWithParams.map((r, stackIndex) =>
                    <React.Fragment key={r.route + r.slot}>{this.modalsByRoute[r.route]?.renderCallback({
                        paramsFromQueryString: r.params, stackIndex,
                        closeFunction: () => this.replaceUrlToCloseARoutedModal(r.route, r.slot)
                    })}</React.Fragment>
                )}
            </RoutedModalsContext.Provider>
        );
    }
}